import { useEffect, useMemo, useRef, useState } from "react";
import type { Category, MenuItem } from "@/lib/data";
import { adminApi, type AdminTenant, type MenuItemInput } from "@/lib/superadmin";
import {
  downloadMenuTemplate,
  downloadMenuXlsx,
  parseMenuSheet,
  planMenuImport,
  rowToPayload,
  type MenuImportPlanRow,
} from "@/lib/menuSheet";
import { PageHeader, Card, GhostButton } from "../components/ui";

/* Superadmin Menu module — pick any restaurant, then manage its categories +
   menu items on its behalf. All data flows through the `superadmin` edge fn. */

type ItemForm = {
  id?: string;
  name: string;
  category: string;
  description: string;
  price: string;
  foodType: MenuItem["foodType"];
  prepTime: string;
  available: boolean;
  recommended: boolean;
};

const blankForm: ItemForm = {
  name: "",
  category: "",
  description: "",
  price: "",
  foodType: "veg",
  prepTime: "",
  available: true,
  recommended: false,
};

export default function SuperadminMenu() {
  // Restaurant picker
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<AdminTenant[]>([]);
  const [searching, setSearching] = useState(false);
  const [chosen, setChosen] = useState<AdminTenant | null>(null);

  // Menu data for the chosen restaurant
  const [categories, setCategories] = useState<Category[]>([]);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Editors
  const [showItemForm, setShowItemForm] = useState(false);
  const [form, setForm] = useState<ItemForm>(blankForm);
  const [newCategory, setNewCategory] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [bulkRows, setBulkRows] = useState<MenuImportPlanRow[]>([]);
  const [bulkErr, setBulkErr] = useState("");
  const bulkFileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const showToast = (m: string) => {
    setToast(m);
    setTimeout(() => setToast(null), 2500);
  };

  // --- restaurant search (debounced) ---
  useEffect(() => {
    if (chosen) return;
    let active = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await adminApi.tenants.list({ search: query, limit: 8 });
        if (active) setResults(res.rows);
      } catch {
        if (active) setResults([]);
      } finally {
        if (active) setSearching(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [query, chosen]);

  // --- load menu when a restaurant is chosen ---
  const loadMenu = async (restaurantId: string) => {
    setLoading(true);
    setError("");
    try {
      const [cats, its] = await Promise.all([
        adminApi.menu.categories(restaurantId),
        adminApi.menu.items(restaurantId),
      ]);
      setCategories(cats.categories);
      setItems(its.items);
      setSelectedIds(new Set());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load menu.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (chosen) void loadMenu(chosen.id);
  }, [chosen]);

  const itemsByCategory = useMemo(() => {
    const map = new Map<string, MenuItem[]>();
    for (const it of items) {
      const key = it.category ?? "Uncategorised";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(it);
    }
    return map;
  }, [items]);

  // Items store the category *id*; resolve it to the display name for headers.
  const catNameById = useMemo(
    () => new Map(categories.map((c) => [c.id, c.name])),
    [categories],
  );

  // --- actions ---
  const openAdd = () => {
    setForm(blankForm);
    setShowItemForm(true);
  };

  const openEdit = (it: MenuItem) => {
    setForm({
      id: it.id,
      name: it.name,
      category: it.category ?? "",
      description: it.description ?? "",
      price: String(it.price),
      foodType: it.foodType,
      prepTime: String(it.prepTime),
      available: it.available,
      recommended: it.recommended,
    });
    setShowItemForm(true);
  };

  const saveItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chosen) return;
    if (!form.name.trim()) {
      setError("Item name is required.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload: MenuItemInput = {
        name: form.name.trim(),
        category: form.category.trim() || null,
        description: form.description.trim() || null,
        price: Number(form.price) || 0,
        foodType: form.foodType,
        prepTime: Number(form.prepTime) || 0,
        available: form.available,
        recommended: form.recommended,
      };
      if (form.id) payload.id = form.id;
      await adminApi.menu.upsertItem(chosen.id, payload);
      setShowItemForm(false);
      showToast(form.id ? "Item updated." : "Item added.");
      await loadMenu(chosen.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save item.");
    } finally {
      setBusy(false);
    }
  };

  const deleteItem = async (it: MenuItem) => {
    if (!chosen) return;
    if (!window.confirm(`Delete "${it.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await adminApi.menu.deleteItem(chosen.id, it.id);
      showToast("Item deleted.");
      await loadMenu(chosen.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete item.");
    } finally {
      setBusy(false);
    }
  };

  // --- selection + bulk delete ---
  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const toggleSelectAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(items.map((i) => i.id)));

  const deleteMany = async (ids: string[], label: string) => {
    if (!chosen || ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} ${label}? This cannot be undone.`)) return;
    setBusy(true);
    setError("");
    try {
      await Promise.all(ids.map((id) => adminApi.menu.deleteItem(chosen.id, id)));
      setSelectedIds(new Set());
      showToast(`Deleted ${ids.length} item${ids.length === 1 ? "" : "s"}.`);
      await loadMenu(chosen.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bulk delete failed.");
    } finally {
      setBusy(false);
    }
  };

  const addCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chosen || !newCategory.trim()) return;
    setBusy(true);
    try {
      await adminApi.menu.addCategory(chosen.id, newCategory.trim());
      setNewCategory("");
      showToast("Category added.");
      await loadMenu(chosen.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add category.");
    } finally {
      setBusy(false);
    }
  };

  // Bulk import via Excel/CSV (all fields). File → preview → resolve categories → save.
  const handleBulkFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    setBulkErr("");
    try {
      const parsed = await parseMenuSheet(file);
      // Diff against the saved menu — only new/changed rows get written.
      const catName = (id: string | null) => (id ? (categories.find((c) => c.id === id)?.name ?? id) : "");
      const rows = planMenuImport(parsed, items, catName);
      if (rows.length === 0) setBulkErr("No rows found. Fill the template and try again.");
      setBulkRows(rows);
    } catch (err) {
      setBulkRows([]);
      setBulkErr(err instanceof Error ? err.message : "Could not read the file.");
    }
  };

  const runBulkImport = async () => {
    // Only write rows that are actually new or changed.
    const valid = bulkRows.filter((r) => r.status === "new" || r.status === "update");
    if (!chosen || valid.length === 0 || busy) return;
    setBusy(true);
    setBulkErr("");
    try {
      // Resolve category names → ids, creating any that don't exist.
      const idByName = new Map(categories.map((c) => [c.name.trim().toLowerCase(), c.id]));
      const newNames = Array.from(new Set(
        valid.map((r) => (r.row.categoryName ?? "").trim()).filter((n) => n && !idByName.has(n.toLowerCase())),
      ));
      for (const n of newNames) {
        const { category } = await adminApi.menu.addCategory(chosen.id, n);
        idByName.set(n.toLowerCase(), category.id);
        setCategories((prev) => [...prev, category]);
      }

      const payloads: MenuItemInput[] = valid.map((plan) => {
        const cn = (plan.row.categoryName ?? "").trim();
        const categoryId = cn
          ? idByName.get(cn.toLowerCase()) ?? null
          : plan.existing?.category ?? null;
        return rowToPayload(plan, categoryId);
      });
      const { inserted } = await adminApi.menu.bulkImport(chosen.id, payloads);
      setShowBulk(false);
      setBulkRows([]);
      showToast(`Imported ${inserted} change${inserted === 1 ? "" : "s"}.`);
      await loadMenu(chosen.id);
    } catch (err) {
      setBulkErr(err instanceof Error ? err.message : "Bulk import failed.");
    } finally {
      setBusy(false);
    }
  };

  const foodDot: Record<MenuItem["foodType"], string> = {
    veg: "border-emerald-500",
    "non-veg": "border-red-500",
    egg: "border-amber-500",
  };

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1400px] mx-auto">
      {toast && (
        <div className="fixed top-20 right-6 z-50 flex items-center gap-2 bg-foreground-900 text-background-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium">
          <i className="ri-check-line text-emerald-400"></i>
          {toast}
        </div>
      )}

      <PageHeader
        title="Menu Management"
        subtitle="Add or edit the menu for any restaurant on the platform"
        actions={
          chosen ? (
            <GhostButton
              onClick={() => {
                setChosen(null);
                setQuery("");
                setItems([]);
                setCategories([]);
              }}
            >
              <i className="ri-arrow-left-line"></i>
              Change Restaurant
            </GhostButton>
          ) : undefined
        }
      />

      {!chosen ? (
        /* ---- Restaurant picker ---- */
        <Card className="max-w-2xl">
          <label className="block text-sm font-medium text-foreground-700 mb-2">
            Select a restaurant
          </label>
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
            <input
              type="text"
              autoFocus
              placeholder="Search by name, owner, or city..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
            />
          </div>

          <div className="mt-3 divide-y divide-background-100 border border-background-100 rounded-md overflow-hidden">
            {searching && (
              <div className="px-4 py-8 text-center text-sm text-foreground-400">
                <i className="ri-loader-4-line animate-spin mr-2"></i>Searching...
              </div>
            )}
            {!searching && results.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-foreground-400">
                No restaurants found.
              </div>
            )}
            {!searching &&
              results.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setChosen(r)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                    <i className="ri-store-2-line text-primary-600"></i>
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-foreground-900 truncate">
                      {r.businessName}
                    </span>
                    <span className="block text-[11px] text-foreground-400">
                      {r.ownerName} &middot; {r.city}
                    </span>
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </button>
              ))}
          </div>
        </Card>
      ) : (
        /* ---- Menu editor for the chosen restaurant ---- */
        <>
          <div className="bg-primary-50 border border-primary-200 rounded-lg px-4 py-3 flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-store-2-line text-primary-600"></i>
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground-900 truncate">
                {chosen.businessName}
              </p>
              <p className="text-[11px] text-foreground-500">
                {chosen.ownerName} &middot; {chosen.city}
              </p>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-accent-100 text-accent-800 text-sm">
              <i className="ri-error-warning-line"></i>
              {error}
            </div>
          )}

          {loading ? (
            <div className="py-20 text-center">
              <i className="ri-loader-4-line text-2xl text-primary-500 animate-spin"></i>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={openAdd}
                  className="px-4 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                  Add Item
                </button>
                <button
                  onClick={() => setShowBulk((s) => !s)}
                  className="px-4 py-2.5 text-sm font-medium text-foreground-600 border border-background-200 hover:bg-background-100 rounded-md transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-upload-2-line"></i>
                  Bulk Import
                </button>
                <button
                  onClick={() => {
                    if (items.length === 0) { showToast("No items to export."); return; }
                    const catName = (id: string | null) => (id ? (categories.find((c) => c.id === id)?.name ?? id) : "");
                    downloadMenuXlsx(`menu_${chosen?.slug ?? "restaurant"}.xlsx`, items, catName);
                  }}
                  className="px-4 py-2.5 text-sm font-medium text-foreground-600 border border-background-200 hover:bg-background-100 rounded-md transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-2-line"></i>
                  Export
                </button>
                {items.length > 0 && (
                  <button
                    onClick={() => void deleteMany(items.map((i) => i.id), "ALL menu items")}
                    disabled={busy}
                    className="px-4 py-2.5 text-sm font-medium text-rose-600 border border-rose-200 hover:bg-rose-50 rounded-md transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-delete-bin-line"></i>
                    Delete all
                  </button>
                )}
                <form onSubmit={addCategory} className="flex items-center gap-2 ml-auto">
                  <input
                    type="text"
                    placeholder="New category"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="w-40 px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                  />
                  <button
                    type="submit"
                    disabled={busy || !newCategory.trim()}
                    className="px-3 py-2.5 text-sm font-medium text-primary-600 border border-primary-200 hover:bg-primary-50 rounded-md transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    Add Category
                  </button>
                </form>
              </div>

              {/* Bulk import panel — Excel/CSV with all fields */}
              {showBulk && (
                <div className="bg-background-50 border border-background-200 rounded-lg p-4 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs text-foreground-500">
                      Download the Excel template, fill in every field, then upload it. Columns:
                      Name, Category, Description, Food Type, Price, GST %, HSN, Prep Time, Available,
                      Bestseller, Dine-in, Takeaway, Online, Variants, Add-ons, Image URL.
                    </p>
                    <button
                      onClick={() => downloadMenuTemplate()}
                      className="px-3 py-2 text-xs font-semibold text-primary-600 border border-primary-200 hover:bg-primary-50 rounded-md cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-file-excel-2-line mr-1"></i>Download Excel template
                    </button>
                  </div>
                  <p className="text-[11px] text-foreground-400">
                    Variants &amp; Add-ons format: <code>Name:Price | Name:Price</code>. Flags accept Yes/No.
                    New categories are created automatically.
                  </p>

                  <input ref={bulkFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleBulkFile} className="hidden" />

                  {bulkErr && <p className="text-xs text-rose-600">{bulkErr}</p>}

                  {bulkRows.length === 0 ? (
                    <button
                      onClick={() => bulkFileRef.current?.click()}
                      className="w-full py-6 rounded-lg border-2 border-dashed border-background-300 hover:border-primary-300 hover:bg-background-100 transition-colors flex flex-col items-center gap-2 cursor-pointer"
                    >
                      <i className="ri-upload-cloud-2-line text-2xl text-foreground-400"></i>
                      <span className="text-sm font-medium text-foreground-600">Upload filled Excel / CSV</span>
                      <span className="text-[11px] text-foreground-400">.xlsx, .xls or .csv</span>
                    </button>
                  ) : (
                    <div className="border border-background-200 rounded-lg overflow-hidden">
                      <div className="flex items-center gap-2 px-2 py-2 text-[11px] bg-background-100 flex-wrap">
                        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                          {bulkRows.filter((r) => r.status === "new").length} new
                        </span>
                        <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">
                          {bulkRows.filter((r) => r.status === "update").length} changed
                        </span>
                        {bulkRows.some((r) => r.status === "unchanged") && (
                          <span className="px-2 py-0.5 rounded-full bg-background-200 text-foreground-500 font-medium">
                            {bulkRows.filter((r) => r.status === "unchanged").length} unchanged · skipped
                          </span>
                        )}
                        {bulkRows.some((r) => r.status === "error") && (
                          <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-medium">
                            {bulkRows.filter((r) => r.status === "error").length} invalid
                          </span>
                        )}
                      </div>
                      <div className="max-h-56 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-background-100 text-left text-foreground-600">
                            <tr>
                              <th className="p-2 font-medium w-24">Status</th>
                              <th className="p-2 font-medium">Name</th>
                              <th className="p-2 font-medium">What changes</th>
                              <th className="p-2 font-medium text-right">Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkRows.filter((r) => r.status !== "unchanged").map((r, i) => (
                              <tr key={i} className="border-t border-background-100">
                                <td className="p-2">
                                  {r.status === "new" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">New</span>}
                                  {r.status === "update" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-700">Changed</span>}
                                  {r.status === "error" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">Invalid</span>}
                                </td>
                                <td className="p-2 text-foreground-800">{r.row.name}</td>
                                <td className="p-2 text-foreground-600 text-xs">
                                  {r.status === "error" && <span className="text-rose-600">{r.message}</span>}
                                  {r.status === "new" && <span className="text-foreground-400">Will be created</span>}
                                  {r.status === "update" && r.changed.join(", ")}
                                </td>
                                <td className="p-2 text-right text-foreground-700">{r.status === "error" ? "" : `₹${r.row.price}`}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-foreground-500">
                      {bulkRows.length > 0 &&
                        `${bulkRows.filter((r) => r.status === "new" || r.status === "update").length} of ${bulkRows.length} rows will be written`}
                    </div>
                    <div className="flex items-center gap-2">
                      {bulkRows.length > 0 && (
                        <button
                          onClick={() => setBulkRows([])}
                          className="px-3 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-md cursor-pointer"
                        >
                          Choose different file
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => { setShowBulk(false); setBulkRows([]); setBulkErr(""); }}
                        className="px-3 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-md cursor-pointer"
                      >
                        Cancel
                      </button>
                      {(() => {
                        const toWrite = bulkRows.filter((r) => r.status === "new" || r.status === "update").length;
                        return (
                          <button
                            onClick={() => void runBulkImport()}
                            disabled={busy || toWrite === 0}
                            className="px-4 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md cursor-pointer disabled:opacity-60"
                          >
                            {busy ? "Importing…" : toWrite === 0 ? "Nothing to import" : `Import ${toWrite} change${toWrite === 1 ? "" : "s"}`}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Categories summary */}
              {categories.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {categories.map((c) => (
                    <span
                      key={c.id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-background-100 text-foreground-600"
                    >
                      {c.icon && <i className={c.icon}></i>}
                      {c.name}
                      <span className="text-foreground-400">({itemsByCategory.get(c.id)?.length ?? 0})</span>
                    </span>
                  ))}
                </div>
              )}

              {/* Items grouped by category */}
              {items.length === 0 ? (
                <div className="bg-background-50 border border-background-200 rounded-lg py-16 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                    <i className="ri-restaurant-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-sm text-foreground-500 font-medium">No menu items yet</p>
                  <p className="text-xs text-foreground-400 mt-1">
                    Add the first item or use bulk import
                  </p>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Selection / bulk-delete bar */}
                  <div className="flex items-center justify-between gap-3 px-1">
                    <label className="flex items-center gap-2 text-xs text-foreground-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        className="rounded border-background-300 text-primary-500 focus:ring-primary-300 cursor-pointer"
                      />
                      Select all ({items.length})
                    </label>
                    {selectedIds.size > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-foreground-500">{selectedIds.size} selected</span>
                        <button
                          onClick={() => void deleteMany([...selectedIds], "selected items")}
                          disabled={busy}
                          className="px-3 py-1.5 text-xs font-semibold text-rose-600 border border-rose-200 hover:bg-rose-50 rounded-md cursor-pointer disabled:opacity-50 whitespace-nowrap"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>Delete selected
                        </button>
                        <button
                          onClick={() => setSelectedIds(new Set())}
                          className="px-2 py-1.5 text-xs font-medium text-foreground-500 hover:bg-background-100 rounded-md cursor-pointer whitespace-nowrap"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                  {Array.from(itemsByCategory.entries()).map(([cat, list]) => (
                    <div key={cat}>
                      <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                        {catNameById.get(cat) ?? cat}
                      </h3>
                      <div className="bg-background-50 border border-background-200 rounded-lg divide-y divide-background-100 overflow-hidden">
                        {list.map((it) => (
                          <div
                            key={it.id}
                            className={`flex items-center gap-3 px-4 py-3 transition-colors ${selectedIds.has(it.id) ? "bg-primary-50/60" : "hover:bg-background-50/60"}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(it.id)}
                              onChange={() => toggleSelect(it.id)}
                              className="rounded border-background-300 text-primary-500 focus:ring-primary-300 cursor-pointer shrink-0"
                            />
                            <span
                              className={`w-4 h-4 border-2 rounded-sm flex items-center justify-center shrink-0 ${foodDot[it.foodType]}`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  it.foodType === "veg"
                                    ? "bg-emerald-500"
                                    : it.foodType === "egg"
                                    ? "bg-amber-500"
                                    : "bg-red-500"
                                }`}
                              ></span>
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground-900 truncate">
                                {it.name}
                                {it.recommended && (
                                  <i className="ri-star-fill text-amber-400 text-xs ml-1.5"></i>
                                )}
                              </p>
                              {it.description && (
                                <p className="text-[11px] text-foreground-400 truncate">
                                  {it.description}
                                </p>
                              )}
                            </div>
                            {!it.available && (
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                                Unavailable
                              </span>
                            )}
                            <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">
                              ₹{it.price.toLocaleString("en-IN")}
                            </span>
                            <button
                              onClick={() => openEdit(it)}
                              className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer"
                              title="Edit"
                            >
                              <i className="ri-edit-line"></i>
                            </button>
                            <button
                              onClick={() => deleteItem(it)}
                              className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                              title="Delete"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Add/Edit item modal */}
          {showItemForm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div
                className="absolute inset-0 bg-foreground-950/40"
                onClick={() => setShowItemForm(false)}
              ></div>
              <form
                onSubmit={saveItem}
                className="relative bg-background-50 rounded-xl border border-background-200 w-full max-w-lg max-h-[85vh] overflow-y-auto"
              >
                <div className="sticky top-0 bg-background-50 border-b border-background-100 px-6 py-4 flex items-center justify-between rounded-t-xl">
                  <h2 className="text-base font-heading font-bold text-foreground-900">
                    {form.id ? "Edit Item" : "Add Item"}
                  </h2>
                  <button
                    type="button"
                    onClick={() => setShowItemForm(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600 hover:bg-background-100 cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                  <Field label="Name">
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={inputCls}
                      autoFocus
                    />
                  </Field>

                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Price (₹)">
                      <input
                        type="number"
                        min="0"
                        value={form.price}
                        onChange={(e) => setForm({ ...form, price: e.target.value })}
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Prep time (min)">
                      <input
                        type="number"
                        min="0"
                        value={form.prepTime}
                        onChange={(e) => setForm({ ...form, prepTime: e.target.value })}
                        className={inputCls}
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Category">
                      <input
                        type="text"
                        list="cat-options"
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        className={inputCls}
                        placeholder="e.g. Starters"
                      />
                      <datalist id="cat-options">
                        {categories.map((c) => (
                          <option key={c.id} value={c.name} />
                        ))}
                      </datalist>
                    </Field>
                    <Field label="Food type">
                      <select
                        value={form.foodType}
                        onChange={(e) =>
                          setForm({ ...form, foodType: e.target.value as MenuItem["foodType"] })
                        }
                        className={inputCls}
                      >
                        <option value="veg">Veg</option>
                        <option value="non-veg">Non-veg</option>
                        <option value="egg">Egg</option>
                      </select>
                    </Field>
                  </div>

                  <Field label="Description">
                    <textarea
                      rows={2}
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className={inputCls}
                    />
                  </Field>

                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.available}
                        onChange={(e) => setForm({ ...form, available: e.target.checked })}
                        className="accent-primary-500"
                      />
                      Available
                    </label>
                    <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.recommended}
                        onChange={(e) => setForm({ ...form, recommended: e.target.checked })}
                        className="accent-primary-500"
                      />
                      Recommended
                    </label>
                  </div>
                </div>

                <div className="sticky bottom-0 bg-background-50 border-t border-background-100 px-6 py-4 flex items-center justify-end gap-3 rounded-b-xl">
                  <button
                    type="button"
                    onClick={() => setShowItemForm(false)}
                    className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-md cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={busy}
                    className="px-4 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md cursor-pointer disabled:opacity-60 flex items-center gap-2"
                  >
                    {busy && <i className="ri-loader-4-line animate-spin"></i>}
                    {form.id ? "Save Changes" : "Add Item"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground-700 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
